import { useEffect, useState } from "react";
import axios, { API } from "@/lib/api";
import { toast } from "sonner";
import {
  Download, Trash2, ArrowLeft, Loader2, Sparkles, Folder, PencilRuler,
} from "lucide-react";
import { getBackendUrl } from "@/lib/backend";
import { formatApiError } from "@/lib/apiError";
import LuxuryConfirmModal from "@/components/LuxuryConfirmModal";
import PortfolioItemStudio from "@/components/PortfolioItemStudio";
import MoodboardCard from "@/components/MoodboardCard";
import PortfolioThemePicker from "@/components/PortfolioThemePicker";
import DesignBriefReviewDialog from "@/components/DesignBriefReviewDialog";

const BACKEND = getBackendUrl();

export default function PortfolioDetail({ portfolio, onBack, onChanged, inIframe }) {
  const [exporting, setExporting] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState("");
  const [removeTarget, setRemoveTarget] = useState(null);
  const [studioItem, setStudioItem] = useState(null);
  // Feb 2026: gate the export behind a Design-Brief review so lookbook
  // copy is never just a raw refined prompt. Opens BEFORE the ZIP is
  // packed; `exportZip` is invoked as the dialog's onConfirm.
  const [briefReviewOpen, setBriefReviewOpen] = useState(false);

  // Pre-mint export URL? The export endpoint is auth'd via cookie/bearer, but
  // in the iframe we hit the same download problem. For now show inline tip.
  useEffect(() => { setDownloadUrl(""); }, [portfolio.id]);

  const exportZip = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API}/studio/portfolios/${portfolio.id}/export`, {
        credentials: "include",
        headers: { Accept: "application/zip" },
      });
      if (!res.ok) {
        let msg = `Export failed (${res.status}).`;
        try {
          const text = await res.text();
          const data = JSON.parse(text);
          // formatApiError handles Pydantic 422 (array of objects)
          // correctly — `data.detail` used to come through directly,
          // producing "[object Object]" toasts on validation errors.
          const parsed = formatApiError(data, "");
          if (parsed) msg = parsed;
        } catch { /* noop */ }
        toast.error(msg);
        return;
      }
      const blob = await res.blob();
      if (!blob || blob.size < 100) {
        toast.error("Empty export.");
        return;
      }
      const url = window.URL.createObjectURL(blob);
      const filename = `${(portfolio.name || "portfolio").replace(/[^A-Za-z0-9._-]+/g, "_")}.zip`;
      if (inIframe) {
        // Sandbox blocks file save → open the blob URL in a new tab.
        setDownloadUrl(url);
        toast.message("Click the download link that just appeared — your sandbox blocks direct file saves.");
        return;
      }
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.rel = "noopener";
      a.style.position = "fixed";
      a.style.left = "-9999px";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        try { document.body.removeChild(a); } catch { /* noop */ }
        try { window.URL.revokeObjectURL(url); } catch { /* noop */ }
      }, 4000);
      toast.success(`Portfolio downloaded · ${(blob.size / 1024).toFixed(1)} KB`);
      // Close the review dialog once the ZIP has left the building.
      setBriefReviewOpen(false);
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err, "Export failed."));
    } finally {
      setExporting(false);
    }
  };

  const openBriefReview = () => {
    if ((portfolio.items || []).length === 0) return;
    setBriefReviewOpen(true);
  };

  const confirmRemove = async () => {
    if (!removeTarget) return;
    try {
      await axios.delete(`${API}/studio/portfolios/${portfolio.id}/items/${removeTarget.id}`);
      toast.success(`Removed ${removeTarget.design_number}`);
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err, "Remove failed."));
    } finally {
      setRemoveTarget(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={onBack}
          data-testid="portfolio-back"
          className="inline-flex items-center gap-1.5 text-xs text-white/65 hover:text-white transition"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> All portfolios
        </button>
        <button
          type="button"
          onClick={openBriefReview}
          disabled={exporting || (portfolio.items || []).length === 0}
          data-testid="portfolio-export-btn"
          className="inline-flex items-center gap-1.5 text-xs px-3.5 py-1.5 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#F3CE5A] transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {exporting ? (
            <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Packing lookbook…</>
          ) : (
            <><Download className="w-3.5 h-3.5" /> Export portfolio</>
          )}
        </button>
      </div>

      <div className="rounded-2xl border border-white/10 bg-[#0F0F0F] p-5">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-1">
          <Folder className="w-3 h-3" /> {portfolio.prefix}-### · {(portfolio.items || []).length} pieces
        </div>
        <h2 className="font-display text-2xl md:text-3xl text-white">{portfolio.name}</h2>
        {portfolio.atelier_name && (
          <div className="text-sm text-white/55 mt-1">{portfolio.atelier_name}</div>
        )}
        {portfolio.description && (
          <p className="text-[12px] text-white/55 mt-2 max-w-2xl">{portfolio.description}</p>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <MoodboardCard portfolio={portfolio} onChanged={onChanged} />
        <PortfolioThemePicker portfolio={portfolio} onChanged={onChanged} />
      </div>

      {downloadUrl && (
        <a
          href={downloadUrl}
          download={`${(portfolio.name || "portfolio").replace(/[^A-Za-z0-9._-]+/g, "_")}.zip`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="portfolio-export-link"
          className="block rounded-xl border border-[#D4AF37]/40 bg-[#1A1814] p-3 text-[12px] text-[#D4AF37] hover:bg-[#211f17] transition"
        >
          <Download className="w-3.5 h-3.5 inline mr-1.5" />
          Your portfolio ZIP is ready — click here to save it (right-click → Save link as…).
        </a>
      )}

      {(portfolio.items || []).length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/15 bg-[#0F0F0F] p-12 text-center">
          <Sparkles className="w-7 h-7 text-[#D4AF37]/60 mx-auto mb-2" />
          <p className="text-sm text-white/65">No pieces yet.</p>
          <p className="text-[11px] text-white/40 mt-1">Go to the Studio tab and use “Save to portfolio” on any render.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {(portfolio.items || []).map((it) => (
            <div
              key={it.id}
              data-testid={`portfolio-item-${it.design_number}`}
              className="group rounded-2xl border border-white/10 bg-[#0F0F0F] hover:border-[#D4AF37]/35 transition overflow-hidden"
            >
              <div className="relative aspect-[4/3] bg-[#0B0B0B] overflow-hidden">
                {it.image_url && (
                  <img
                    src={`${BACKEND}${it.image_url}`}
                    alt={it.design_number}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                )}
                <div className="absolute top-2 left-2 bg-black/65 backdrop-blur rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] border border-[#D4AF37]/30">
                  {it.design_number}
                </div>
              </div>
              <div className="p-3">
                {it.style && <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">{it.style}</div>}
                <p className="text-[12px] text-white/70 mt-1 line-clamp-2">
                  {(it.prompt || "—").slice(0, 140)}{(it.prompt || "").length > 140 ? "…" : ""}
                </p>
                {it.materials?.price_range && (
                  <div className="text-[11px] text-white/55 mt-2">
                    Est: {it.materials.price_range.currency || "INR"}{" "}
                    {it.materials.price_range.low ?? "—"} – {it.materials.price_range.high ?? "—"}
                  </div>
                )}
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-[10px] text-white/40">
                    {it.materials ? "BOM included" : "No BOM saved"}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setStudioItem(it)}
                      data-testid={`portfolio-item-studio-${it.design_number}`}
                      className="text-[10px] text-[#D4AF37] hover:text-[#F3CE5A] inline-flex items-center gap-1 transition"
                    >
                      <PencilRuler className="w-3 h-3" /> Specs · Drafting
                    </button>
                    <button
                      type="button"
                      onClick={() => setRemoveTarget(it)}
                      data-testid={`portfolio-item-remove-${it.design_number}`}
                      className="text-[10px] text-white/40 hover:text-red-400 inline-flex items-center gap-1 transition"
                    >
                      <Trash2 className="w-3 h-3" /> Remove
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <LuxuryConfirmModal
        open={!!removeTarget}
        onOpenChange={(v) => !v && setRemoveTarget(null)}
        title="Remove from portfolio?"
        description={`${removeTarget?.design_number || ""} will be removed from this portfolio. The render itself stays in your Studio.`}
        confirmLabel="Remove"
        onConfirm={confirmRemove}
        destructive
      />

      <PortfolioItemStudio
        open={!!studioItem}
        onOpenChange={(v) => !v && setStudioItem(null)}
        portfolio={portfolio}
        item={studioItem}
        onChanged={onChanged}
      />

      <DesignBriefReviewDialog
        open={briefReviewOpen}
        onOpenChange={setBriefReviewOpen}
        portfolio={portfolio}
        onConfirm={exportZip}
        exporting={exporting}
      />
    </div>
  );
}
